import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import {
  Colors,
  Field,
  Link,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { addParamsToUrl } from '@zapier/url-utils';
import { urls, useSize } from '@zapier/identity-utils';
import { AuthButton } from '@zapier/authentication';

import { useForgotPassword } from './useForgotPassword';
import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';

const errorStyle = css`
  ${Typography.base}
  color: ${Colors.error};
`;

const messageStyle = css`
  ${Typography.paragraph1}
  color: ${Colors.gray};
  text-align: center;
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.success};
`;

export function ForgotPasswordPage() {
  const router = useRouter();
  const size = useSize();
  const { canSubmit, email, error, handleChangeEmail, handleSubmit, status } =
    useForgotPassword();

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title="Reset your password"
      titlePosition="right"
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        <p css={messageStyle}>
          Enter the email address you signed up with below. An email will be
          sent containing a link to reset your password.
        </p>
        <p css={messageStyle}>
          Forgot which email address you signed up with?{' '}
          <Link
            href={addParamsToUrl(urls.HELP_URL, {
              account: 'settings',
              from_url: router.asPath,
              settings: 'reset',
              topic: 'account',
            })}
          >
            Get help
          </Link>
          .
        </p>

        <Field
          label="Email"
          inputId="reset_password_email"
          isDisabled={status === 'submitting'}
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              autoComplete="email"
              inputMode="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChangeEmail(e.target.value)
              }
              placeholder="Email"
              type="email"
              value={email}
            />
          )}
        />
        <AuthButton
          disabled={!canSubmit || status !== 'initial'}
          isBusy={status === 'submitting'}
          type="submit"
        >
          Send reset instructions
        </AuthButton>
        {error && <div css={errorStyle}>{error}</div>}
        {status === 'success' && <div css={successStyle}>Sent!</div>}
      </form>
    </AuthLayout>
  );
}
